if (!customElements.get("product-info")) {
  customElements.define(
    "product-info",
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector(".quantity__input");
      }

      connectedCallback() {
        this.initializeProductSwapUtility();
        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this),
        );

        this.initQuantityHandlers();
        this.initSelectUpdatedVariantButtons();
        this.dispatchEvent(
          new CustomEvent("product-info:loaded", { bubbles: true }),
        );
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector(".product-form__quantity");
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(
            PUB_SUB_EVENTS.cartUpdate,
            this.fetchQuantityRules.bind(this),
          );
        }
      }

      // Initialize select-updated-variant buttons (simplified)
      initSelectUpdatedVariantButtons() {
        const buttons = this.querySelectorAll(".select-updated-variant");
        buttons.forEach((button) => {
          if (!button.dataset.listenerAdded) {
            button.addEventListener(
              "click",
              this.handleSelectUpdatedVariantClick.bind(this),
            );
            button.dataset.listenerAdded = "true";
          }
        });
      }

      // Handle click on select-updated-variant buttons (simplified)
      handleSelectUpdatedVariantClick(event) {
        const button = event.currentTarget;
        const variantId = button.dataset.variantId;
        const productUrl = button.dataset.productUrl;

        if (!variantId || !productUrl) return;

        this.updateSelectUpdatedVariantButtonState(button, "loading");

        // Just update the variant directly
        this.updateVariantInputs(variantId);
        this.updateURL(productUrl, variantId);

        setTimeout(() => {
          this.updateSelectUpdatedVariantButtonState(button, "default");
        }, 1000);
      }

      // Update select-updated-variant button state (simplified)
      updateSelectUpdatedVariantButtonState(button, state) {
        if (state === "loading") {
          button.disabled = true;
          button.classList.add("loading");
        } else {
          button.disabled = false;
          button.classList.remove("loading");
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html
            .querySelectorAll(".scroll-trigger")
            .forEach((element) =>
              element.classList.add("scroll-trigger--cancel"),
            ),
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
          // Re-initialize select-updated-variant buttons after DOM update
          this.initSelectUpdatedVariantButtons();
        });
      }

      handleOptionValueChange({
        data: { event, target, selectedOptionValues },
      }) {
        if (
          !this.contains(event.target) &&
          !event.target.closest(".select-updated-variant")
        )
          return;

        const targetIdNew = `ProductSubmitButton-quickadd-${this.sectionId}`;

        const observer = new MutationObserver(() => {
          document
            .querySelectorAll(`#${CSS.escape(targetIdNew)}`)
            .forEach((btn) => {
              btn.disabled = true;
              btn.classList.add("button--disabled");
              btn.setAttribute("disabled", "disabled"); // HTML attribute
              btn.setAttribute("aria-disabled", "true");
              btn.style.opacity = "0.5";
              btn.style.pointerEvents = "none";
            });
        });

        observer.observe(document.body, {
          childList: true,
          subtree: true,
        });
        this.resetProductFormState();

        const productUrl =
          target.dataset.productUrl ||
          this.pendingRequestUrl ||
          this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage =
          this.dataset.updateUrl === "true" && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(
            productUrl,
            selectedOptionValues,
            shouldFetchFullPage,
          ),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage
            ? "product-info[id^='MainProduct']"
            : "product-info";
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector("head title").innerHTML =
              html.querySelector("head title").innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector("main"),
              html.querySelector("main"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector("product-info"),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks,
            );
          }

          // Update select-updated-variant buttons after swap
          this.updateSelectUpdatedVariantButtons(html, variant);
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
            // Update select-updated-variant button states (simplified)
            const buttons = this.querySelectorAll(".select-updated-variant");
            buttons.forEach((button) => {
              this.updateSelectUpdatedVariantButtonState(button, "default");
            });
          })
          .catch((error) => {
            if (error.name === "AbortError") {
              console.log("Fetch aborted by user");
            } else {
              console.error(error);
              // Update select-updated-variant button states on error (simplified)
              const buttons = this.querySelectorAll(".select-updated-variant");
              buttons.forEach((button) => {
                this.updateSelectUpdatedVariantButtonState(button, "default");
              });
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector(
          "variant-selects [data-selected-variant]",
        )?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(
        url,
        optionValues,
        shouldFetchFullPage = false,
      ) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(",")}`);
        }

        return `${url}?${params.join("&")}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector("variant-selects");
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(
            this.variantSelectors,
            variantSelects,
            this.preProcessHtmlCallbacks,
          );

          scrollSelectedVariantIntoView();
        }
      }

      // Update select-updated-variant buttons with new variant data (simplified)
      updateSelectUpdatedVariantButtons(html, variant) {
        const buttons = this.querySelectorAll(".select-updated-variant");
        buttons.forEach((button) => {
          if (variant?.id) {
            button.dataset.variantId = variant.id;
          }
        });
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (
            id,
            shouldHide = (source) => false,
          ) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(
              `#${id}-${this.dataset.section}`,
            );
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle("hidden", shouldHide(source));
            }
          };

          updateSourceFromDestination("price");
          updateSourceFromDestination("Sku", ({ classList }) =>
            classList.contains("hidden"),
          );
          updateSourceFromDestination(
            "Inventory",
            ({ innerText }) => innerText === "",
          );
          updateSourceFromDestination("Volume");
          updateSourceFromDestination("Price-Per-Item", ({ classList }) =>
            classList.contains("hidden"),
          );

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(
            `#Quantity-Rules-${this.dataset.section}`,
          )?.classList.remove("hidden");
          this.querySelector(
            `#Volume-Note-${this.dataset.section}`,
          )?.classList.remove("hidden");

          // this.productForm?.toggleSubmitButton(
          //   html
          //     .getElementById(`ProductSubmitButton-${this.sectionId}`)
          //     ?.hasAttribute("disabled") ?? true,
          //   window.variantStrings.soldOut,
          // );
          const isVariantUnavailable =
            html
              .getElementById(`ProductSubmitButton-${this.sectionId}`)
              ?.hasAttribute("disabled") ?? true;

          this.productForm?.toggleSubmitButton(
            isVariantUnavailable,
            window.variantStrings.soldOut,
          );

          if (window.location.pathname.includes('thriveco-5-minutes-coloring-shampoo-natural-black-400ml')) {
            const soldOutText = window.variantStrings?.soldOut || 'Sold out';
            const addToCartText = window.variantStrings?.addToCart || 'Add to cart';

            // 1. Normal submit buttons (.product-form__submit inside .normal-button or .non-popup)
            document.querySelectorAll('.product-form__submit[name="add"]').forEach((btn) => {
              const textSpan = btn.querySelector('span:first-child');
              if (isVariantUnavailable) {
                btn.disabled = true;
                btn.setAttribute('aria-disabled', 'true');
                if (textSpan) textSpan.textContent = soldOutText;
              } else {
                btn.disabled = false;
                btn.removeAttribute('aria-disabled');
                if (textSpan) textSpan.textContent = addToCartText;
              }
              if (variant?.id) btn.setAttribute('data-popup-variant', variant.id);
            });

            // 2. Popup trigger buttons (.quick-add__submit.from-pdp) — type="button", open modal
            document.querySelectorAll('.quick-add__submit.from-pdp').forEach((btn) => {
              if (isVariantUnavailable) {
                btn.disabled = true;
                btn.setAttribute('aria-disabled', 'true');
                const atcTextEl = btn.querySelector('.atc-text');
                const popupCu = btn.querySelector('.product-atc-popup-cu');
                if (atcTextEl) {
                  atcTextEl.textContent = soldOutText;
                  const priceContainer = btn.querySelector('.atc-btn-container');
                  if (priceContainer) priceContainer.style.display = 'none';
                } else if (popupCu) {
                  const spinner = btn.querySelector('.loading-overlay__spinner');
                  popupCu.innerHTML = soldOutText;
                  if (spinner) btn.appendChild(spinner);
                }
              } else {
                btn.disabled = false;
                btn.removeAttribute('aria-disabled');
                const atcTextEl = btn.querySelector('.atc-text');
                const priceContainer = btn.querySelector('.atc-btn-container');
                if (atcTextEl) {
                  atcTextEl.textContent = addToCartText;
                  if (priceContainer) priceContainer.style.display = '';
                }
              }
              if (variant?.id) btn.setAttribute('data-variant-id', variant.id);
            });

            // 3. Sticky ATC bar button
            const stickyBtn = document.getElementById('sticky-add-to-cart');
            if (stickyBtn) {
              const firstText = stickyBtn.childNodes[0];
              if (isVariantUnavailable) {
                stickyBtn.disabled = true;
                stickyBtn.setAttribute('aria-disabled', 'true');
                stickyBtn.classList.add('is-sold-out');
                if (firstText && firstText.nodeType === Node.TEXT_NODE) firstText.textContent = soldOutText + ' ';
              } else {
                stickyBtn.disabled = false;
                stickyBtn.removeAttribute('aria-disabled');
                stickyBtn.classList.remove('is-sold-out');
                if (firstText && firstText.nodeType === Node.TEXT_NODE) firstText.textContent = 'ADD TO CART ';
              }
            }
          }

          const targetIdNew = `ProductSubmitButton-quickadd-${this.sectionId}`;

          const observer = new MutationObserver(() => {
            document
              .querySelectorAll(`#${CSS.escape(targetIdNew)}`)
              .forEach((btn) => {
                // condition to enable/disable (replace with your real logic)
                btn.disabled = false;
                btn.classList.remove("button--disabled");
                btn.removeAttribute("aria-disabled");
                btn.removeAttribute("disabled");
                btn.style.opacity = "";
                btn.style.pointerEvents = "";
              });
          });

          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });

          // Update select-updated-variant buttons
          this.updateSelectUpdatedVariantButtons(html, variant);

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`,
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? "";
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector("share-button")?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ""}`,
        );

        if (this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          `${url}${variantId ? `?variant=${variantId}` : ""}`,
        );
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(
          true,
          window.variantStrings.unavailable,
        );

        const selectors = [
          "price",
          "Inventory",
          "Sku",
          "Price-Per-Item",
          "Volume-Note",
          "Volume",
          "Quantity-Rules",
        ]
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(", ");
        document
          .querySelectorAll(selectors)
          .forEach(({ classList }) => classList.add("hidden"));

        // Update select-updated-variant buttons to unavailable state (simplified)
        const buttons = this.querySelectorAll(".select-updated-variant");
        buttons.forEach((button) => {
          button.disabled = true;
        });
      }

      updateThumbnails(html, variantFeaturedMediaId) {
        const destULs = html.querySelectorAll(
          "media-gallery ul.thumbnail-list, media-gallery ul.slider--mobile",
        );
        const sourceULs = this.querySelectorAll(
          "media-gallery ul.thumbnail-list, media-gallery ul.slider--mobile",
        );

        const SPECIAL_CLASS = "new_special_variant_show_class";

        // Remove special class from source
        sourceULs.forEach((ul) => {
          const items = ul.querySelectorAll(
            "li[data-target], li[data-media-id]",
          );
          items.forEach((li) => li.classList.remove(SPECIAL_CLASS));
        });

        const specialClassMap = {};

        // Read classes from destination
        destULs.forEach((ul) => {
          const items = ul.querySelectorAll(
            "li[data-target], li[data-media-id]",
          );
          items.forEach((li) => {
            const mediaId = li.dataset.target || li.dataset.mediaId;
            if (li.classList.contains(SPECIAL_CLASS)) {
              specialClassMap[mediaId] = true;
            }
          });
        });

        // Add same classes to source
        sourceULs.forEach((ul) => {
          const items = ul.querySelectorAll(
            "li[data-target], li[data-media-id]",
          );
          items.forEach((li) => {
            const mediaId = li.dataset.target || li.dataset.mediaId;
            if (specialClassMap[mediaId]) {
              li.classList.add(SPECIAL_CLASS);
            }
          });
        });
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector("media-gallery ul");
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);
        this.updateThumbnails(html, variantFeaturedMediaId);

        const refreshSourceData = () => {
          if (this.hasAttribute("data-zoom-on-hover")) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(
            mediaGallerySource.querySelectorAll("li[data-media-id]"),
          );
          const sourceSet = new Set(
            mediaGallerySourceItems.map((item) => item.dataset.mediaId),
          );
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [
              item.dataset.mediaId,
              { item, index },
            ]),
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] =
            refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll("li[data-media-id]"),
          );
          const destinationSet = new Set(
            mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId),
          );
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (
              !sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)
            ) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (
              !destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)
            ) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh)
            [mediaGallerySourceItems, sourceSet, sourceMap] =
              refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach(
            (destinationItem, destinationIndex) => {
              const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

              if (sourceData && sourceData.index !== destinationIndex) {
                mediaGallerySource.insertBefore(
                  sourceData.item,
                  mediaGallerySource.querySelector(
                    `li:nth-of-type(${destinationIndex + 1})`,
                  ),
                );

                // refresh source now that it has been modified
                [mediaGallerySourceItems, sourceSet, sourceMap] =
                  refreshSourceData();
              }
            },
          );
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true,
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(
          `.product-media-modal__content`,
        );
        const newModalContent = html.querySelector(
          `product-modal .product-media-modal__content`,
        );
        if (modalContent && newModalContent)
          modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity
            ? parseInt(this.quantityInput.dataset.cartQuantity)
            : 0,
          min: this.quantityInput.dataset.min
            ? parseInt(this.quantityInput.dataset.min)
            : 1,
          max: this.quantityInput.dataset.max
            ? parseInt(this.quantityInput.dataset.max)
            : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute("max");
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector(
          ".quantity__rules-cart .loading__spinner",
        ).classList.remove("hidden");
        fetch(
          `${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`,
        )
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html",
            );
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() =>
            this.querySelector(
              ".quantity__rules-cart .loading__spinner",
            ).classList.add("hidden"),
          );
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(
          `Quantity-Form-${sectionId}`,
        );
        const selectors = [
          ".quantity__input",
          ".quantity__rules",
          ".quantity__label",
        ];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === ".quantity__input") {
            const attributes = [
              "data-cart-quantity",
              "data-min",
              "data-max",
              "step",
            ];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector("variant-selects");
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "related-products",
        );
        return document.querySelector(
          `product-recommendations[data-section-id^="${relatedProductsSectionId}"]`,
        );
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          "quick_order_list",
        );
        return document.querySelector(
          `quick-order-list[data-id^="${quickOrderListSectionId}"]`,
        );
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    },
  );
}


// ── AtcPricePlugin ────────────────────────────────────────────────────────────
// Standalone plugin: updates .cleanup-mrp and .cleanup-compare-price elements.
// Completely isolated — does NOT touch ProductInfo or any other Shopify code.
// Safe to remove without affecting any other functionality.
// ─────────────────────────────────────────────────────────────────────────────
(function AtcPricePlugin() {
  let priceCents = 0;
  let compareCents = 0;
  let activeRootKey = null;
  const rootPriceMap = new Map(); // rootKey -> { variantId, priceCents, compareCents }

  const format = (cents) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(cents / 100);

  function getQuickAddRoots() {
    return Array.from(document.querySelectorAll('[id^="QuickAddInfo-"]'));
  }

  function getMainRoots() {
    return Array.from(document.querySelectorAll('product-info[data-section]'));
  }

  function getRootKey(root) {
    if (!root) return null;
    if (root.id && root.id.startsWith('QuickAddInfo-')) return `qa:${root.id}`;
    if (root.matches?.('product-info')) {
      return `main:${root.dataset?.section || root.id || 'default'}`;
    }
    return null;
  }

  function getActiveRoot() {
    if (activeRootKey?.startsWith('qa:')) {
      const qaId = activeRootKey.replace('qa:', '');
      const qaRoot = document.getElementById(qaId);
      if (qaRoot) return qaRoot;
    }
    if (activeRootKey?.startsWith('main:')) {
      const sectionId = activeRootKey.replace('main:', '');
      const mainRoot = document.querySelector(`product-info[data-section="${sectionId}"]`);
      if (mainRoot) return mainRoot;
    }
    return document.querySelector('product-info[data-section]');
  }

  function getRootFromNode(node) {
    if (!(node instanceof Element)) return null;
    const quickAddRoot = node.closest('[id^="QuickAddInfo-"]');
    if (quickAddRoot) return quickAddRoot;
    return node.closest('product-info');
  }

  function readVariantFromRoot(root) {
    if (!root) return null;
    const selectedNode = root.querySelector('[data-selected-variant]');
    if (!selectedNode) return null;

    try {
      return JSON.parse(selectedNode.innerHTML || selectedNode.textContent || '{}');
    } catch (e) {
      return null;
    }
  }

  function readVariantIdFromRoot(root) {
    if (!root) return null;
    const variantInput = root.querySelector('input[name="id"].product-variant-id, input[name="id"]');
    const raw = variantInput?.value;
    if (!raw) return null;
    const id = parseInt(String(raw).trim(), 10);
    return Number.isFinite(id) ? id : null;
  }

  function getPricesForRoot(root) {
    const rootKey = getRootKey(root);
    const currentVariantId = readVariantIdFromRoot(root);

    if (rootKey && rootPriceMap.has(rootKey)) {
      const state = rootPriceMap.get(rootKey);
      if (!currentVariantId || !state?.variantId || state.variantId === currentVariantId) {
        return state;
      }
    }

    const rootVariant = readVariantFromRoot(root);
    if (rootVariant && rootKey) {
      const next = {
        variantId: rootVariant.id || currentVariantId || null,
        priceCents: rootVariant.price || 0,
        compareCents: rootVariant.compare_at_price || 0,
      };
      rootPriceMap.set(rootKey, next);
      return next;
    }

    return { priceCents, compareCents };
  }

  function syncPricesFromRootVariant(root) {
    const rootKey = getRootKey(root);
    const rootVariant = readVariantFromRoot(root);
    if (!rootVariant) return false;

    const next = {
      variantId: rootVariant.id || readVariantIdFromRoot(root) || null,
      priceCents: rootVariant.price || 0,
      compareCents: rootVariant.compare_at_price || 0,
    };

    if (rootKey) {
      rootPriceMap.set(rootKey, next);
      activeRootKey = rootKey;
    }

    priceCents = next.priceCents;
    compareCents = next.compareCents;
    return true;
  }

  function syncAndRenderRoot(root, changedInput) {
    if (!root) return;
    syncPricesFromRootVariant(root);
    updateElements(changedInput, root, false);
  }

  // Reset qty to min (or 1) for only the current root when variant changes.
  function resetQtyForRoot(root) {
    if (!root) return null;

    const qtyInputs = Array.from(root.querySelectorAll('.quantity__input'));
    if (!qtyInputs.length) return null;

    let changedInput = null;
    qtyInputs.forEach((qtyInput) => {
      const minAttr = parseInt(qtyInput.getAttribute('min') || qtyInput.dataset?.min || '1', 10);
      const nextQty = Number.isFinite(minAttr) && minAttr > 0 ? minAttr : 1;

      qtyInput.value = String(nextQty);
      qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
      qtyInput.dispatchEvent(new Event('change', { bubbles: true }));

      if (!changedInput || qtyInput.offsetParent !== null) {
        changedInput = qtyInput;
      }
    });

    // Keep hidden quantity inputs in sync for add-to-cart forms in the same root.
    const fallbackQty = changedInput ? parseInt(changedInput.value || '1', 10) : 1;
    const nextQty = Number.isFinite(fallbackQty) && fallbackQty > 0 ? fallbackQty : 1;
    root.querySelectorAll('input#quantity__input[type="hidden"][name="quantity"]').forEach((hidden) => {
      hidden.value = String(nextQty);
      hidden.setAttribute('value', String(nextQty));
    });

    return changedInput;
  }

  // Get qty only from the same root context (main or popup), never cross-context
  function getQty(root, fromInput) {
    if (fromInput && root?.contains(fromInput)) {
      const directQty = parseInt(fromInput.value || '1', 10);
      return Number.isFinite(directQty) && directQty > 0 ? directQty : 1;
    }

    const inputs = root?.querySelectorAll('.quantity__input') || [];
    for (const input of inputs) {
      if (input.offsetParent !== null) {
        const visibleQty = parseInt(input.value || '1', 10);
        return Number.isFinite(visibleQty) && visibleQty > 0 ? visibleQty : 1;
      }
    }

    const fallbackQty = parseInt(inputs[0]?.value || '1', 10);
    return Number.isFinite(fallbackQty) && fallbackQty > 0 ? fallbackQty : 1;
  }

  function updateElements(changedInput, root = getActiveRoot(), includeQuickAddRoots = true) {
    const roots = [];
    if (root) roots.push(root);
    if (includeQuickAddRoots) {
      roots.push(...getQuickAddRoots());
    }

    roots.forEach((targetRoot) => {
      const scopedPrices = getPricesForRoot(targetRoot);
      const scopedPriceCents = scopedPrices.priceCents || 0;
      const scopedCompareCents = scopedPrices.compareCents || 0;
      if (!scopedPriceCents) return;

      const qty = getQty(targetRoot, changedInput);

      targetRoot.querySelectorAll('.cleanup-mrp').forEach(el => {
        el.textContent = format(scopedPriceCents * qty);
      });

      targetRoot.querySelectorAll('.cleanup-compare-price').forEach(el => {
        if (scopedCompareCents && scopedCompareCents > scopedPriceCents) {
          el.textContent = format(scopedCompareCents * qty);
          el.style.display = 'block';
        } else {
          el.style.display = 'none';
        }
      });
    });
  }

  // Initialize prices from the variant already selected on page load
  document.addEventListener('DOMContentLoaded', () => {
    const root = getActiveRoot();
    const variantEl =
      root?.querySelector('[data-selected-variant]') ||
      document.querySelector('[id^="QuickAddInfo-"] [data-selected-variant]');
    if (variantEl) {
      try {
        const variant = JSON.parse(variantEl.innerHTML);
        priceCents = variant.price || 0;
        compareCents = variant.compare_at_price || 0;
        const rootKey = getRootKey(root);
        activeRootKey = rootKey;
        if (rootKey) {
          rootPriceMap.set(rootKey, {
            variantId: variant.id || readVariantIdFromRoot(root) || null,
            priceCents,
            compareCents,
          });
        }
        updateElements(undefined, root);
      } catch (e) {}
    }

    // Event delegation — catches ALL quantity inputs (mobile, desktop, popup)
    document.addEventListener('change', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.matches('.quantity__input')) {
        const scopedRoot = getRootFromNode(target) || getActiveRoot();
        activeRootKey = getRootKey(scopedRoot) || activeRootKey;
        updateElements(target, scopedRoot, false); // only this context
      }

      // Fallback: when hidden variant input changes, re-read selected variant JSON for that root.
      if (
        target.matches('input[name="id"].product-variant-id') ||
        target.matches('variant-selects select') ||
        target.matches('variant-radios input[type="radio"]')
      ) {
        const scopedRoot = getRootFromNode(target) || getActiveRoot();
        if (!scopedRoot) return;
        requestAnimationFrame(() => {
          syncAndRenderRoot(scopedRoot, undefined);
        });
        setTimeout(() => {
          syncAndRenderRoot(scopedRoot, undefined);
        }, 120);
      }
    });

    // Also catch input event (Dawn's +/- buttons fire 'input' not 'change')
    document.addEventListener('input', (e) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (target.matches('.quantity__input')) {
        const scopedRoot = getRootFromNode(target) || getActiveRoot();
        activeRootKey = getRootKey(scopedRoot) || activeRootKey;
        updateElements(target, scopedRoot, false); // only this context
      }
    });
  });

  // Update when variant changes via Shopify PUB_SUB
  subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
    const variant = event?.data?.variant;
    if (!variant) return;
    const sectionId = event?.data?.sectionId || null;

    priceCents = variant.price || 0;
    compareCents = variant.compare_at_price || 0;

    const rootFromEvent = getRootFromNode(event?.data?.event?.target || event?.data?.target || null);
    const rootFromSection = sectionId ? document.querySelector(`product-info[data-section="${sectionId}"]`) : null;
    const resolvedRoot = rootFromEvent || rootFromSection || null;

    const rootKey = getRootKey(resolvedRoot);
    if (rootKey) {
      rootPriceMap.set(rootKey, {
        variantId: variant.id || readVariantIdFromRoot(resolvedRoot) || null,
        priceCents,
        compareCents,
      });
      activeRootKey = rootKey;
    }

    if (resolvedRoot) {
      const changedInput = resetQtyForRoot(resolvedRoot);
      syncAndRenderRoot(resolvedRoot, changedInput);
      return;
    }

    // Fallback: when event has no target/root, refresh all known contexts from DOM.
    [...getMainRoots(), ...getQuickAddRoots()].forEach((root) => {
      const changedInput = resetQtyForRoot(root);
      syncAndRenderRoot(root, changedInput);
    });
  });
})();
